import { PlacaNoCartaz } from './TrancaDeOrdem.js';
import { Relogio } from './Relogio.js';
import { fraseDaEspera, type Lancamento } from '../lib/lancamento.js';

/**
 * ════════════════════════════════════════════════════════════════════════════
 * ── «SÓ VOCÊ VÊ» — O CARTAZ QUE EXISTE NA BANCADA E NÃO NA VITRINE ─────────
 * ════════════════════════════════════════════════════════════════════════════
 *
 * **Ordem do dono, 04/11/2026:**
 *
 * > *«eu criei um curso de design patterns de agente IA e coloquei para ele não
 * > aparecer na vitrine e ele tem uma data pra ser exibido, mas eu coloquei ele
 * > no design da vitrine nos v-members, e ele deveria ao menos aparecer na
 * > vitrine da edição uma capa com aviso de que ele está agendado e bloqueado
 * > na sua visualização»*
 *
 * ## O que estava a acontecer, medido no banco de produção no mesmo dia
 *
 * `Padrões de Design para Agentes IA` — `status = 'draft'`, `available_at` dois
 * dias à frente, **quatro aulas publicadas lá dentro** — estava numa prateleira
 * manual («Engenharia de IA») que tinha esse item e mais nenhum. A régua do core
 * recusa o rascunho na primeira pergunta (*«um rascunho não é de ninguém»*), e
 * essa lei não se toca: ela é o que impede o curso em construção de aparecer na
 * turma inteira. O resultado, porém, era a prévia da bancada a desenhar a faixa
 * **vazia**, com a frase «solte aqui» — indistinguível de um arraste que falhou.
 *
 * ## Por que um carimbo PRÓPRIO, e não o «Agendado» que já existe
 *
 * Porque são dois fatos, e o tutor precisa dos dois:
 *
 * | carimbo | o que afirma | quem o lê |
 * |---|---|---|
 * | `SeloDeLancamento` («Agendado») | este curso abre num dia marcado | o ALUNO, na vitrine |
 * | `SeloSoVoceVe` («Só você vê») | este cartão não existe para o aluno | o TUTOR, na bancada |
 *
 * Um curso **anunciado** (publicado, com data no futuro) tem o primeiro e não o
 * segundo: o aluno vê o cartaz, trancado, com a data. Um curso **guardado**
 * (rascunho, com data) tem os dois fatos — e é por isso que este carimbo VENCE
 * o outro no canto da capa, enquanto a frase dentro do véu continua a ser a da
 * estreia. O teto de um carimbo por cartaz é lei desta prateleira desde que a
 * terceira espécie fechada nasceu, e quebrá-lo aqui empilharia duas chapas de
 * 10px no mesmo canto de um cartão de 176px.
 *
 * A ordem entre os dois não é arbitrária: «Agendado» sozinho faria o tutor
 * concluir que o aluno já vê o cartaz à espera do dia — que é exactamente o
 * engano que a ordem do dono veio corrigir, só do outro lado.
 *
 * ## Por que a redação mora AQUI, e não no core
 *
 * Porque este texto é do INSTRUMENTO, não do produto: ele só se desenha na
 * prévia da bancada, que é o painel, e nunca chega a aluno nenhum (o servidor só
 * manda o campo em `?fonte=rascunho`). É a mesma decisão — e a mesma gramática —
 * da faixa «Prévia — só quem tem acesso ao painel vê esta página», que vive em
 * `rotas/Promo.tsx` em português, sem passar pelo dicionário de seis línguas.
 *
 * O que o servidor manda é o FATO (`soVoceVe: true`), e nada mais. Mandar a
 * frase pronta obrigaria a rota a saber como a bancada escreve — e a frase da
 * estreia, essa sim, continua a ser derivada do core no relógio de quem lê.
 */

/** O carimbo sobre a arte. Vence o «Agendado» — ver o cabeçalho. */
export function SeloSoVoceVe() {
  return <PlacaNoCartaz data-vst-so-voce-ve="selo">Só você vê</PlacaNoCartaz>;
}

/**
 * A frase dentro do véu, no sítio onde a estreia e a tranca já falam.
 *
 * Com data marcada, ela é a da ESTREIA — derivada no relógio de quem lê, como
 * em qualquer outro cartaz agendado (`EstadoNoCartaz`), porque é a frase certa e
 * duas redações da mesma data divergiriam no primeiro dia em que uma mudasse.
 * Sem data, ela diz o estado que sobra: rascunho, e o gesto que o resolve.
 *
 * O relógio acompanha a data e **não** acompanha o rascunho: ali não há hora
 * nenhuma a anunciar, e o glifo prometeria uma.
 */
/**
 * ── E O MESMO FATO NO DESTAQUE (04/11/2026, a segunda metade da leva) ───────
 *
 * O herói é a maior peça da vitrine, e é onde o engano custa mais: o tutor que
 * arrastava o curso guardado para o destaque via o cartaz do curso seguinte — ou
 * nada — enquanto a faixa de baixo já o desenhava com o carimbo. Duas peças da
 * mesma prévia a responder coisas diferentes sobre o mesmo curso.
 *
 * ## Por que uma peça PRÓPRIA, e não `PlacaNoCartaz` outra vez
 *
 * Porque aquela é `absolute left-2 top-2` — ela é o canto DO CARTÃO, e o lugar
 * faz parte do que ela é. No herói o que existe é a fila do gesto primário, e é
 * exactamente ela que tem de sair: **«Começar» num curso que o aluno não vê é a
 * maior promessa da página a apontar para uma porta que não existe para ninguém
 * além de quem está a olhar.** O que fica no lugar é o motivo — a mesma decisão,
 * com as mesmas palavras, que `MotivoDaTranca` já toma neste sítio.
 *
 * O véu, a arte sem cor e a frase da estreia continuam a ser os do cartaz: o que
 * esta peça acrescenta é o carimbo e a linha, no registo do bloco grande.
 */
export function SoVoceVeNoHeroi({ lancamento }: { lancamento: Lancamento | null }) {
  return (
    <div
      data-vst-so-voce-ve="heroi"
      className="inline-flex max-w-xl flex-wrap items-center gap-x-3 gap-y-1.5 px-3.5 py-2.5"
      style={{
        /* A MESMA chapa da placa do cartão (`--fundo` a 82%): é ela que faz «este
           cartaz tem um estado» ler-se de longe nas duas peças. */
        background: 'color-mix(in srgb, var(--fundo) 82%, transparent)',
        borderRadius: 'var(--raio)',
      }}
    >
      <span
        data-vst-so-voce-ve="selo"
        className="text-[10px] font-medium uppercase tracking-[0.14em]"
      >
        Só você vê
      </span>
      <span className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--texto-suave)' }}>
        {/* O relógio acompanha a data e não acompanha o rascunho, pela razão
            escrita em `SoVoceVeNoCartaz`: ali não há hora a anunciar. */}
        {lancamento ? (
          <>
            <Relogio />
            <span data-vst-lancamento="frase">{fraseDaEspera(lancamento)}</span>
          </>
        ) : (
          <span>Rascunho — publique para o aluno ver</span>
        )}
      </span>
    </div>
  );
}

export function SoVoceVeNoCartaz({ lancamento }: { lancamento: Lancamento | null }) {
  if (!lancamento) {
    return <span className="truncate">Rascunho — publique para o aluno ver</span>;
  }
  return (
    <span className="flex items-center gap-1.5">
      <Relogio />
      <span data-vst-lancamento="frase" className="truncate">
        {fraseDaEspera(lancamento)}
      </span>
    </span>
  );
}
